import produce from 'immer'
import { assign, get } from 'lodash'

import {
  FETCH_INGREDIENT,
  SET_INGREDIENT_PAGE_TO_DIRTY,
  UPDATE_INGREDIENT_ALTERNATIVE_INGREDIENTS_DETAILS,
  UPDATE_INGREDIENT_COMPONENT_DETAILS,
  UPDATE_INGREDIENT_EXTRA_DETAILS,
  UPDATE_INGREDIENT_IMAGE_DETAILS,
  UPDATE_INGREDIENT_NOTES,
  UPDATE_INGREDIENT_OVERRIDES_INGREDIENTS_DETAILS,
  UPDATE_INGREDIENT_SIMPLE_DETAILS,
} from '../action-types'
import { INGREDIENT_INITIAL_STATE } from '../initial-states'

const ingredientReducer = (state = INGREDIENT_INITIAL_STATE, action) => {
  switch (action.type) {
    case SET_INGREDIENT_PAGE_TO_DIRTY:
      return produce(state, nextState => assign(nextState, action.payload))
    case FETCH_INGREDIENT.START:
      return produce(state, nextState =>
        assign(nextState, {
          isFetchLoading: true,
          fetchError: undefined,
          data: [],
        }),
      )

    case FETCH_INGREDIENT.COMPLETE:
      return produce(state, nextState =>
        assign(nextState, {
          isFetchLoading: false,
          fetchError: undefined,
          data: action.payload.data.ingredient,
        }),
      )

    case FETCH_INGREDIENT.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isFetchLoading: false,
          fetchError: action.error.message,
          data: [],
        }),
      )

    case FETCH_INGREDIENT.CLEANUP:
      return { ...INGREDIENT_INITIAL_STATE }

    case UPDATE_INGREDIENT_SIMPLE_DETAILS.START:
      return produce(state, nextState =>
        assign(nextState, {
          isSimpleDetailsLoading: true,
          simpleDetailsError: undefined,
        }),
      )

    case UPDATE_INGREDIENT_SIMPLE_DETAILS.COMPLETE:
      return produce(state, nextState =>
        assign(nextState, {
          isSimpleDetailsLoading: false,
          simpleDetailsError: undefined,
          data: assign(
            nextState.data,
            action.payload.data.updateIngredientSimpleDetails,
          ),
        }),
      )

    case UPDATE_INGREDIENT_SIMPLE_DETAILS.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isSimpleDetailsLoading: false,
          simpleDetailsError: action.error.message,
        }),
      )

    case UPDATE_INGREDIENT_IMAGE_DETAILS.START:
      return produce(state, nextState =>
        assign(nextState, {
          isImageDetailsLoading: true,
          imageDetailsError: undefined,
        }),
      )

    case UPDATE_INGREDIENT_IMAGE_DETAILS.COMPLETE:
      return produce(state, nextState =>
        assign(nextState, {
          isImageDetailsLoading: false,
          imageDetailsError: undefined,
          data: assign(
            nextState.data,
            action.payload.data.updateIngredientImageDetails,
          ),
        }),
      )

    case UPDATE_INGREDIENT_IMAGE_DETAILS.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isImageDetailsLoading: false,
          imageDetailsError: action.error.message,
        }),
      )

    case UPDATE_INGREDIENT_NOTES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isNotesLoading: true,
          notesError: undefined,
        }),
      )

    case UPDATE_INGREDIENT_NOTES.COMPLETE:
      return produce(state, nextState =>
        assign(nextState, {
          isNotesLoading: false,
          notesError: undefined,
          data: assign(
            nextState.data,
            action.payload.data.updateIngredientNotes,
          ),
        }),
      )

    case UPDATE_INGREDIENT_NOTES.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isNotesLoading: false,
          notesError: action.error.message,
        }),
      )

    case UPDATE_INGREDIENT_EXTRA_DETAILS.START:
      return produce(state, nextState =>
        assign(nextState, {
          isExtraDetailsLoading: true,
          extraDetailsError: undefined,
        }),
      )

    case UPDATE_INGREDIENT_EXTRA_DETAILS.COMPLETE:
      return produce(state, nextState => {
        assign(nextState, {
          isExtraDetailsLoading: false,
          extraDetailsError: undefined,
          data: assign(
            nextState.data,
            action.payload.data.updateIngredientExtraDetails,
          ),
        })
      })

    case UPDATE_INGREDIENT_EXTRA_DETAILS.FAIL:
      return produce(state, nextState => {
        const { graphQLErrors, message } = action.error

        const errorDetails = get(graphQLErrors, '[0].extensions.duplicated')

        return assign(nextState, {
          isExtraDetailsLoading: false,
          extraDetailsError: {
            errorDetails,
            message,
          },
        })
      })

    case UPDATE_INGREDIENT_COMPONENT_DETAILS.START:
      return produce(state, nextState =>
        assign(nextState, {
          isComponentDetailsLoading: true,
          componentDetailsError: undefined,
        }),
      )

    case UPDATE_INGREDIENT_COMPONENT_DETAILS.COMPLETE:
      return produce(state, nextState =>
        assign(nextState, {
          isComponentDetailsLoading: false,
          componentDetailsError: undefined,
          data: assign(
            nextState.data,
            action.payload.data.updateIngredientComponentDetails,
          ),
        }),
      )

    case UPDATE_INGREDIENT_COMPONENT_DETAILS.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isComponentDetailsLoading: false,
          componentDetailsError: action.error.message,
        }),
      )

    case UPDATE_INGREDIENT_OVERRIDES_INGREDIENTS_DETAILS.START:
      return produce(state, nextState =>
        assign(nextState, {
          isOverridesIngredientsDetailsLoading: true,
          overridesIngredientsDetailsError: undefined,
        }),
      )

    case UPDATE_INGREDIENT_OVERRIDES_INGREDIENTS_DETAILS.COMPLETE:
      return produce(state, nextState =>
        assign(nextState, {
          isOverridesIngredientsDetailsLoading: false,
          overridesIngredientsDetailsError: undefined,
          data: assign(
            nextState.data,
            action.payload.data.updateIngredientOverridesIngredientsDetails,
          ),
        }),
      )

    case UPDATE_INGREDIENT_OVERRIDES_INGREDIENTS_DETAILS.FAIL:
      return produce(state, nextState => {
        const { message, graphQLErrors } = action.error

        const conflicts = get(
          graphQLErrors,
          '[0].extensions.exception.conflicts',
        )

        return assign(nextState, {
          isOverridesIngredientsDetailsLoading: false,
          overridesIngredientsDetailsError: {
            message,
            conflicts,
          },
        })
      })

    case UPDATE_INGREDIENT_ALTERNATIVE_INGREDIENTS_DETAILS.START:
      return produce(state, nextState =>
        assign(nextState, {
          isAlternativeIngredientsDetailsLoading: true,
          alternativeIngredientsDetailsError: undefined,
        }),
      )

    case UPDATE_INGREDIENT_ALTERNATIVE_INGREDIENTS_DETAILS.COMPLETE:
      return produce(state, nextState =>
        assign(nextState, {
          isAlternativeIngredientsDetailsLoading: false,
          alternativeIngredientsDetailsError: undefined,
          data: assign(
            nextState.data,
            action.payload.data.updateIngredientAlternativeIngredientsDetails,
          ),
        }),
      )

    case UPDATE_INGREDIENT_ALTERNATIVE_INGREDIENTS_DETAILS.FAIL:
      return produce(state, nextState => {
        const { message, graphQLErrors } = action.error

        const conflicts = get(
          graphQLErrors,
          '[0].extensions.exception.conflicts',
        )

        return assign(nextState, {
          isAlternativeIngredientsDetailsLoading: false,
          alternativeIngredientsDetailsError: {
            message,
            conflicts,
          },
        })
      })

    default:
      return state
  }
}

export default ingredientReducer
